import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { supabase } from "@/lib/supabase";
import MinhaLojaCard from "@/components/MinhaLojaCard";
import CityAutocomplete from "@/components/CityAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, User, BadgeCheck, Mail, Crown } from "lucide-react";

const STATES = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];


function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}
export default function ProfilePage() {
  const { user, setUser, authLoading } = useApp();
  const [form, setForm] = useState({ name: "", phone: "", city: "", state: "" });
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [verif, setVerif] = useState<any>(null);
  const [plan, setPlan] = useState<any>(null);
  const [canceling, setCanceling] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelAgree, setCancelAgree] = useState(false);
  useEffect(() => {
    if (!user) return;
    supabase.rpc("my_verification_status").then(({ data }) => setVerif(data || null));
    supabase.rpc("my_plan_summary").then(({ data }) => setPlan(data || null));
  }, [user?.id]);

  async function handleCancel() {
    if (!user) return;
    setCanceling(true);
    const { data, error } = await supabase.functions.invoke("cancel-subscription", { body: { userId: user.id } });
    setCanceling(false);
    if (error || !(data as any)?.ok) {
      alert("Não foi possível cancelar agora. " + ((data as any)?.error === "no_stripe_subscription" ? "Este plano não possui assinatura recorrente ativa." : "Tente novamente ou contate o suporte."));
      return;
    }
    supabase.rpc("my_plan_summary").then(({ data }) => setPlan(data || null));
    setCancelModal(false); setCancelAgree(false);
    supabase.rpc("my_plan_summary").then(({ data }) => setPlan(data || null));
  }

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" />;

  useEffect(() => {
    loadProfile();
  }, [user]);

  async function loadProfile() {
    const { data } = await supabase
      .rpc("get_my_profile")
      .single();
    if (data) {
      setForm({
        name: data.name || "",
        phone: formatPhone(data.phone || ""),
        city: data.city || "",
        state: data.state || "",
      });
      setAvatarUrl(data.avatar_url || null);
    }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `${user.id}/avatar.${ext}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
    if (!error) {
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      const url = `${data.publicUrl}?t=${Date.now()}`;
      setAvatarUrl(url);
      await supabase.from("profiles").update({ avatar_url: url }).eq("id", user.id);
      // Atualizar nome nos anúncios existentes não é necessário pois o avatar é buscado pelo perfil
    }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.name || !form.phone || !form.city || !form.state) {
      setError("Preencha todos os campos."); return;
    }
    setSaving(true);
    setError("");
    const { error } = await supabase
      .from("profiles")
      .update({
        name: form.name,
        phone: form.phone,
        city: form.city,
        state: form.state,
      })
      .eq("id", user.id);

    // Atualiza tambem o metadata do Auth (fonte lida no reload pelo mapSupabaseUser)
    if (!error) {
      await supabase.auth.updateUser({
        data: { name: form.name, phone: form.phone, city: form.city, state: form.state },
      });
    }

    if (error) {
      setError("Erro ao salvar. Tente novamente.");
    } else {
      // Atualiza o user global para a Navbar refletir o novo nome na hora
      setUser((prev) => prev ? { ...prev, name: form.name, phone: form.phone, city: form.city, state: form.state } : prev);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    }
    setSaving(false);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-lg">
      <h1 className="text-2xl font-heading font-bold mb-6">Meu Perfil</h1>

      {verif && (
        <div className="mb-6 bg-card border border-border rounded-xl p-5">
          <h2 className="text-sm font-heading font-bold mb-3 flex items-center gap-2">
            <BadgeCheck className="h-4 w-4 text-primary" /> Verificação da conta
          </h2>
          <div className="space-y-3">
            {/* Nível 1 - E-mail */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${verif.email_confirmed ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                  {verif.email_confirmed ? <BadgeCheck className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                </div>
                <div>
                  <p className="text-sm font-medium">E-mail confirmado</p>
                  <p className="text-xs text-muted-foreground">{verif.email_confirmed ? "Seu e-mail está verificado." : "Confirme para aumentar a confiança."}</p>
                </div>
              </div>
              {verif.email_confirmed
                ? <span className="text-xs font-semibold text-green-700">✓ Ativo</span>
                : <span className="text-xs text-amber-700">Pendente</span>}
            </div>

            {/* Nível 2 - Anunciante Verificado */}
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-border">
              <div className="flex items-center gap-2">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${verif.is_verified_advertiser ? "bg-blue-100 text-blue-700" : "bg-muted text-muted-foreground"}`}>
                  <Crown className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">Anunciante Verificado</p>
                  <p className="text-xs text-muted-foreground">
                    {verif.is_verified_advertiser
                      ? "Selo azul nos seus anúncios e mais confiança."
                      : "Assine o plano Pro ou Ultra para receber o selo."}
                  </p>
                </div>
              </div>
              {verif.is_verified_advertiser
                ? <span className="text-xs font-semibold text-blue-700">✓ Verificado</span>
                : <a href="/planos" className="text-xs font-semibold bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700">Ver planos</a>}
            </div>

            {plan && !plan.is_admin && plan.plan_id !== "free" && plan.billing_cycle === "monthly" && plan.has_stripe && (
              <div className="pt-3 border-t border-border">
                {plan.cancel_at_period_end ? (
                  <p className="text-xs text-amber-700">
                    Renovação cancelada. Seu plano {plan.plan_name} permanece ativo até {plan.period_end ? new Date(plan.period_end).toLocaleDateString("pt-BR") : "o fim do período"}.
                  </p>
                ) : (
                  <button onClick={() => setCancelModal(true)}
                    className="text-xs font-medium text-destructive hover:underline">
                    Cancelar renovação automática
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {plan && !plan.is_admin && (
        <div className="mb-6 bg-card border border-border rounded-xl p-5">
          <h2 className="text-sm font-heading font-bold mb-3 flex items-center gap-2">
            <Crown className="h-4 w-4 text-primary" /> Seu plano
          </h2>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-lg font-bold capitalize">{plan.plan_name || plan.plan_id}</p>
              {plan.plan_id !== "free" && plan.billing_cycle && (
                <p className="text-xs text-muted-foreground">Cobrança {plan.billing_cycle === "yearly" ? "anual" : "mensal"}</p>
              )}
            </div>
            {plan.plan_id === "free"
              ? <a href="/planos" className="text-xs font-semibold bg-primary text-primary-foreground px-3 py-1.5 rounded-md hover:bg-primary/90">Fazer upgrade</a>
              : plan.period_end && (
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      {plan.cancel_at_period_end ? "Ativo até" : "Renova em"}
                    </p>
                    <p className="text-sm font-semibold">{new Date(plan.period_end).toLocaleDateString("pt-BR")}</p>
                  </div>
                )}
          </div>

          {/* Consumo */}
          <div className="space-y-2">
            {(() => {
              const bar = (used: number, limit: number, label: string, icon: string) => {
                const unlimited = limit === -1;
                const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
                const full = !unlimited && used >= limit;
                return (
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">{icon} {label}</span>
                      <span className={full ? "text-destructive font-medium" : "text-foreground"}>
                        {used}/{unlimited ? "∞" : limit}
                      </span>
                    </div>
                    {!unlimited && (
                      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${full ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                      </div>
                    )}
                  </div>
                );
              };
              return (
                <>
                  {bar(plan.listings_used ?? 0, plan.listings_limit ?? 3, "Anúncios este mês", "📋")}
                  {bar(plan.featured_used ?? 0, plan.featured_limit ?? 0, "Destaques este mês", "⭐")}
                </>
              );
            })()}
          </div>

          {plan.cancel_at_period_end && (
            <p className="text-xs text-amber-700 mt-3 pt-3 border-t border-border">
              Renovação automática cancelada. Seu plano permanece ativo até o fim do período.
            </p>
          )}
        </div>
      )}

      <MinhaLojaCard plan={plan} />

      <div className="bg-card border border-border rounded-lg p-6 space-y-6">
        {/* Avatar */}
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div className="h-24 w-24 rounded-full overflow-hidden bg-muted border-2 border-border flex items-center justify-center">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
              ) : (
                <User className="h-12 w-12 text-muted-foreground" />
              )}
            </div>
            <label className="absolute bottom-0 right-0 h-8 w-8 bg-accent rounded-full flex items-center justify-center cursor-pointer hover:bg-accent/90 transition-colors">
              <Camera className="h-4 w-4 text-accent-foreground" />
              <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} disabled={uploading} />
            </label>
          </div>
          <p className="text-sm text-muted-foreground">
            {uploading ? "Enviando foto..." : "Clique na camera para alterar"}
          </p>
        </div>

        {/* Formulário */}
        {error && <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</p>}
        {success && <p className="text-sm text-green-600 bg-green-50 p-3 rounded-md">Perfil atualizado com sucesso!</p>}

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Nome completo *</label>
            <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Telefone *</label>
            <Input placeholder="(xx) xxxxx-xxxx" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: formatPhone(e.target.value) }))} />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">E-mail</label>
            <Input value={user.email} disabled className="opacity-60" />
            <p className="text-xs text-muted-foreground mt-1">O e-mail não pode ser alterado</p>
          </div>
          <CityAutocomplete city={form.city} state={form.state} onSelect={(city, state) => setForm(p => ({ ...p, city, state }))} />
        </div>

        <Button
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Salvando..." : "Salvar alterações"}
        </Button>
      </div>

      {cancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setCancelModal(false)}>
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-heading font-bold mb-2">Cancelar renovação automática</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Ao cancelar, sua assinatura <strong>não será renovada</strong> no próximo ciclo. Seu plano continua
              ativo normalmente até o fim do período já pago
              {plan?.period_end ? ` (${new Date(plan.period_end).toLocaleDateString("pt-BR")})` : ""}.
              Não há reembolso proporcional do período em curso.
            </p>
            <label className="flex items-start gap-2 mb-5 cursor-pointer select-none">
              <input type="checkbox" checked={cancelAgree} onChange={(e) => setCancelAgree(e.target.checked)}
                className="h-4 w-4 accent-primary mt-0.5" />
              <span className="text-xs text-muted-foreground">
                Li e aceito as condições de cancelamento descritas acima.
              </span>
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setCancelModal(false); setCancelAgree(false); }}
                className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary">
                Voltar
              </button>
              <button onClick={handleCancel} disabled={!cancelAgree || canceling}
                className="px-4 py-2 text-sm rounded-md bg-destructive text-white font-medium hover:bg-destructive/90 disabled:opacity-50">
                {canceling ? "Cancelando..." : "Confirmar cancelamento"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
